import {types, Instance, getParent, getEnv, getSnapshot} from "mobx-state-tree";
import queryString from "qs";


import type {RootStoreModel} from "@stores/RootStore";
import {getRedirectUrlAndRemoveItFromSearchParams} from "../actions/redirect";
import {PageAdditionalParams, setPageAdditionalParams} from "../volatile/pageAdditionalParams";
import {registerQuizVisit} from "../actions/metric/metricActions";


import {MODALS} from "../ui/screens/modals/modal-names";
import {unsavedChangesManager} from "./UnsavedChangesManager";
import {screenSizeProvider} from "./ScreenSizeProvider";


import {WorkspaceModel, WorkspaceModelType} from "./WorkspaceStore";
import {Snapshot, SnapshotHeading} from "../@types/document-addition";
import {selectWorkspaceFromPlain} from "../actions/ui/selectWorkspace";
import {RootStoreEnv} from "@stores/createStore";


export const PAGES = {
    KNOWLEDGE_BASE: 'knowledge_base',
    DOC_EDIT: 'docEdit',
    DOC_HISTORY: 'docHistory',
    SIGN_IN: 'sign-in',
    SIGN_UP: 'sign-up',
    RESET_PASSWORD: 'reset-password',
    SETTINGS: 'settings',
    SETTINGS_MEMBERS: 'stgMembers',
    SETTINGS_ACCESSES: 'stgAccesses',
    SETTINGS_GENERAL: 'stgGeneral',
    SETTINGS_BILLING: 'stgBilling',
    SETTINGS_PROFILE: 'stgProfile',
    WORKSPACE_SETTINGS_GENERAL: 'wsGeneral',
    WORKSPACE_MEMBERS: 'wsMembers',
    QUIZZES: 'quizzes',
    QUIZ_EDIT: 'quizEdit',
    QUIZ_VIEW: 'quizView',
    QUIZ_TAKE: 'quizTake',
    CHARTFLOW: 'chartflow',
    ACKNOWLEDGMENTS_OVERVIEW: 'acknowledgments_overview',
    LISTS: 'lists',
} as const;

// Pages considered part of the Settings section (show SettingsSecondaryNav)
export const settingsPages = [
    PAGES.SETTINGS_GENERAL,
    PAGES.SETTINGS_MEMBERS,
    PAGES.SETTINGS_ACCESSES,
    PAGES.WORKSPACE_SETTINGS_GENERAL,
    PAGES.WORKSPACE_MEMBERS,
    PAGES.SETTINGS_PROFILE,
] as const;

// Knowledge Base pages that require TreeLayout and RightMenu
export const kbPages = [
    PAGES.KNOWLEDGE_BASE,
    PAGES.DOC_EDIT,
    PAGES.DOC_HISTORY,
] as const;

export interface CommonParams {
    team?: string;
    wsSlug?: string;
    libraryId?: string;
    libraryName?: string;
}

export interface EditDocParams extends CommonParams {
    id?: string;
    itemName?: string;
}

export interface DocHistoryParams extends CommonParams {
    id?: string;
    snapshot?: Snapshot;
    snapshotHeading?: SnapshotHeading
}

export interface EditTestParams extends CommonParams {
    id?: string;
}


export type PagePathParamsMap = {
    'knowledge_base': CommonParams;
    'docEdit': EditDocParams;
    'docHistory': DocHistoryParams;
    'sign-in': CommonParams;
    'sign-up': CommonParams;
    'reset-password': CommonParams;
    'settings': CommonParams;
    'stgMembers': CommonParams;
    'stgAccesses': CommonParams;
    'stgGeneral': CommonParams;
    'stgBilling': CommonParams;
    'stgProfile': CommonParams;
    'wsGeneral' : CommonParams;
    'wsMembers' : CommonParams;
    'quizzes': CommonParams;
    'quizEdit': EditTestParams;
    'quizView': EditTestParams;
    'quizTake': EditTestParams;
    'chartflow': CommonParams;
    'acknowledgments_overview': CommonParams;
    'lists': CommonParams;
};

export type PageName = keyof PagePathParamsMap;


const DEFAULT_PAGENAME = PAGES.KNOWLEDGE_BASE;

const publicLinks = ['sign-in', 'sign-up', 'reset-password'];

const formatItemUrlPart = (name: string | undefined | null, id: string) => {
    return `${encodeURIComponent(name || '')}-${id}`;
};


export const ViewStore = types.model("ViewStore",
    {
        workspace: types.optional(types.maybeNull(WorkspaceModel), null),
        page: types.optional(types.string, DEFAULT_PAGENAME),
        hash: types.frozen(),
        query: types.frozen<Record<string, string>>({}),
        team: types.frozen(),
        pathParams: types.frozen(),
    })
    .views(self => ({
        get rootStore() {
            return getParent(self)
        },
        getUrlByName<T extends keyof PagePathParamsMap>(pageName: T, additionalParams?: PagePathParamsMap[T]) {
            const dsManager = getEnv<RootStoreEnv>(self).getDsManager();
            const userState = getEnv<RootStoreEnv>(self).getUserState();
            const client = dsManager?.clientAR?.row;
            const workspaceDB = (() => {
                if (additionalParams?.wsSlug) {
                    return userState?.getWsBySlug(additionalParams.wsSlug);
                } else if (self.workspace?.slug){
                    return userState?.getWsBySlug(self.workspace.slug)
                } else {
                    return null;
                }
            })()
            const activeWorkspace = self.workspace as WorkspaceModelType | null;
            const currentPathParams = self.pathParams as Partial<CommonParams> | undefined;
            const getLibraryUrlSegment = (params?: CommonParams) => {
                const libraryId = params?.libraryId
                    || activeWorkspace?.selectedLibrary?.id
                    || currentPathParams?.libraryId
                    || activeWorkspace?.selectedLibraryId
                    || activeWorkspace?.childIds[0];

                if (!libraryId || !workspaceDB) {
                    return '';
                }

                const libraryFromWorkspace = activeWorkspace?.items.get(libraryId);
                const itemsDS = dsManager?.getItemsDataSource(workspaceDB.id);
                const libraryFromDS = itemsDS?.allRows.find(itemRow => itemRow.id === libraryId);
                const libraryName = params?.libraryName
                    || libraryFromWorkspace?.name
                    || libraryFromDS?.name
                    || '';

                return `/libraries/${formatItemUrlPart(libraryName, libraryId)}`;
            };

            if (!client) {
                switch (pageName) {
                    case PAGES.SIGN_IN:
                    case PAGES.SIGN_UP:
                    case PAGES.RESET_PASSWORD:
                        if (self.team) {
                            return `/${self.team}/${pageName}`;
                        } else {
                            return `/${pageName}`;
                        }
                    default:
                        if (self.team) {
                            return `/${self.team}/`;
                        } else {
                            return '/';
                        }
                }
            } else {
                switch (pageName) {
                    case PAGES.KNOWLEDGE_BASE: {
                        if (workspaceDB) {
                            const librarySegment = getLibraryUrlSegment(additionalParams as CommonParams | undefined);
                            return `/${client.slug}/${workspaceDB?.slug}${librarySegment || '/'}`;
                        } else {
                            return `/${client.slug}/`;
                        }
                    }
                    case PAGES.DOC_EDIT:
                    case PAGES.DOC_HISTORY: {
                        const targetId = (additionalParams as EditDocParams)?.id || self.workspace?.selectedId;
                        const nameFromParams = (additionalParams as EditDocParams)?.itemName;
                        const namePartInUrl = nameFromParams || (() => {
                            const itemsDS = workspaceDB && dsManager && dsManager.getItemsDataSource(workspaceDB.id);
                            const targetItem = targetId && itemsDS && itemsDS.allRows.find((itemRow) => itemRow.id === targetId);
                            return targetItem ? targetItem.name : '';
                        })();
                        const escapedNamePartInUrl = encodeURIComponent(namePartInUrl || '');
                        const librarySegment = getLibraryUrlSegment(additionalParams as CommonParams | undefined);
                        if (pageName === PAGES.DOC_HISTORY) {
                            return `/${client.slug}/${workspaceDB?.slug}${librarySegment}/${escapedNamePartInUrl}-${targetId}/history`;
                        } else {
                            return `/${client.slug}/${workspaceDB?.slug}${librarySegment}/${escapedNamePartInUrl}-${targetId}`;
                        }
                    }
                    case PAGES.SETTINGS:
                        return `/${client.slug}/settings`;
                    case PAGES.SETTINGS_GENERAL:
                        return `/${client.slug}/settings/general`;
                    case PAGES.SETTINGS_MEMBERS:
                        return `/${client.slug}/settings/members`;
                    case PAGES.SETTINGS_BILLING:
                        return `/${client.slug}/settings/billing`;
                    case PAGES.SETTINGS_PROFILE:
                        return `/${client.slug}/settings/profile`;
                    case PAGES.WORKSPACE_SETTINGS_GENERAL:
                        return `/${client.slug}/${workspaceDB?.slug}/settings/general`;
                    case PAGES.WORKSPACE_MEMBERS:
                        return `/${client.slug}/${workspaceDB?.slug}/settings/members`;
                    case PAGES.QUIZZES:
                        return `/${client.slug}/${workspaceDB?.slug}/tests`;
                    case PAGES.QUIZ_VIEW:
                    case PAGES.QUIZ_TAKE:
                    case PAGES.QUIZ_EDIT: {
                        const targetId = (additionalParams as EditTestParams)?.id;
                        const targetTest = targetId
                            ? targetId
                            : null;
                        if (targetTest && pageName === PAGES.QUIZ_EDIT) {
                            return `/${client.slug}/${workspaceDB?.slug}/tests/${targetTest}/edit`;
                        }
                        if (targetTest && pageName === PAGES.QUIZ_TAKE) {
                            return `/${client.slug}/${workspaceDB?.slug}/tests/${targetTest}/take`;
                        }
                        if (targetTest && pageName === PAGES.QUIZ_VIEW) {
                            return `/${client.slug}/${workspaceDB?.slug}/tests/${targetTest}`;
                        }
                        return `/${client.slug}/${workspaceDB?.slug}/tests/`;
                    }
                    case PAGES.ACKNOWLEDGMENTS_OVERVIEW:
                        return `/${client.slug}/${workspaceDB?.slug}/acknowledgments`;
                    case PAGES.LISTS:
                        return `/${client.slug}/${workspaceDB?.slug}/lists`;
                    case PAGES.CHARTFLOW:
                        return `/${client.slug}/${workspaceDB?.slug}/chartflow`;
                    default:
                        return `/${client.slug}/${workspaceDB?.slug}/${pageName}`;
                }
            }
        },
        get pageId() {
            const pageName = self.page;
            switch (pageName) {
                case 'docEdit': {
                    const activeWorkspace = self.workspace as WorkspaceModelType | null;
                    const selectedItemId = activeWorkspace?.selectedId;
                    return `${pageName}-${selectedItemId}`;
                }
                case 'workspace':
                default:
                    return pageName;
            }
        },
        get hasHash() {
            return !!self.hash;
        },
        get hasQuery() {
            return Object.keys(self.query).length !== 0;
        },
        get currentQuery() {
            return queryString.stringify(self.query);
        },
    }))
    .views(self => ({
        get currentPath() {
            return import.meta.env.VITE_BASE_PATH + self.getUrlByName(self.page as PageName, self.pathParams);
        },
        getFullUrl<T extends keyof PagePathParamsMap>(pageName: T, additionalParams?: PagePathParamsMap[T]) {
            return import.meta.env.VITE_BASE_PATH + self.getUrlByName(pageName, additionalParams);
        }
    }))
    .views(self => ({
        get currentUrl() {
            return self.currentPath + (self.hasQuery ? '?' + self.currentQuery : '')
                + (self.hasHash ? '#' + self.hash : '');
        },
    }))
    .actions(self => ({
        resetState() {
            self.page = DEFAULT_PAGENAME
            self.hash = ''
            self.query = {}
        },
        setWorkspace(workspace: WorkspaceModelType | null) {
            self.workspace = workspace;
        },
        setPage(pageName: PageName) {
            const userState = getEnv<RootStoreEnv>(self).getUserState();
            self.page = pageName;
            if (!userState?.user) {
                history.pushState(null, '', self.currentUrl);
            }
        },
        setQuery(query: {}) {
            self.query = query;
        },
        setHash(hash: string | undefined) {
            self.hash = hash;
            window.location.hash = `#${hash}`;
        },
        redirectIfNeeded(pageName: PageName): boolean {
            const env = getEnv<RootStoreEnv>(self);
            const userState = env.getUserState();
            const routerService = env.routerService();

            if (!self.query.redirectTo && userState?.user) {
                // no redirect exists and non-needed
                return false;
            }

            if ((!userState?.user) && !publicLinks.includes(pageName)) {
                // needs to redirect if user has no active session but opens private page
                self.query = {
                    ...self.query,
                    redirectTo: window.location.href,
                };

                const redirectPage = self.query.link ? 'sign-up' : 'sign-in';
                routerService.navigate(redirectPage as PageName, undefined, self.query);
                return true;
            }

            if (self.query.redirectTo && userState?.user && publicLinks.includes(pageName)) {
                // exising redirect handle if user had an active session and the link is auth link
                const url = getRedirectUrlAndRemoveItFromSearchParams(self as ViewStoreModel);
                const parsedURl = new URL(url);
                routerService.navigateByPath(parsedURl.pathname, parsedURl.search);
                return true;
            }

            if (self.query.redirectTo && userState?.user && !publicLinks.includes(pageName)) {
                // Ignore and remove redirect from non-auth links
                const clearedQuery = {...self.query};
                delete clearedQuery.redirectTo;
                self.query = {...clearedQuery};
            }

            return false; // No redirection needed;
        }
    }))
    .actions(self => ({
            openPageByName<T extends PageName>(
                pageName: T,
                pathParams?: PagePathParamsMap[T],
                queryParams: Record<string, string> = self.query,
                additionalParams?: PageAdditionalParams,
                hashString?: string,
            ) {
                const isMobile = screenSizeProvider.view !== 'desktop';
                const {getUserState, setModal, setRightMenu, toggleIsTreeCollapsed, getDsManager} = getEnv<RootStoreEnv>(self);
                const userState = getUserState();

                if (unsavedChangesManager.hasUnsavedChanges) {
                    if (self.page === PAGES.QUIZ_TAKE) {
                        setModal({name: MODALS.UNSAVED_CHANGE_QUIZ_TAKE_CONFIRM,
                            params:{
                                openPageByNameArguments:
                                    [
                                        pageName,
                                        pathParams,
                                        queryParams,
                                        additionalParams,
                                    ]
                            }});
                    } else {
                        setModal({name: MODALS.UNSAVED_CHANGE_CONFIRM,
                            params:{
                                openPageByNameArguments:
                                    [
                                        pageName,
                                        pathParams,
                                        queryParams,
                                        additionalParams,
                                    ]
                            }});
                    }
                    return;
                }
                self.query = queryParams;
                self.hash = hashString;
                self.pathParams = pathParams;
                setPageAdditionalParams(additionalParams);
                if (pathParams?.team) {
                    self.team = pathParams.team;
                }

                if (userState) {
                    if (publicLinks.includes(pageName)) {
                        //redirect to main page if user already signed in
                        pageName = PAGES.KNOWLEDGE_BASE as T;
                    }

                    let workspace = pathParams?.wsSlug ? userState!.getWsBySlug(pathParams.wsSlug) : null;
                    if (!workspace && self.workspace === null && userState.availableWorkspaces.length) {
                        workspace = userState!.availableWorkspaces[0];
                    }
                    if (workspace) {
                        selectWorkspaceFromPlain(self.rootStore as RootStoreModel, getDsManager()!, workspace)
                    }
                }

                if (userState && publicLinks.includes(pageName)) {
                    //redirect to main page if user already signed in
                    pageName = PAGES.KNOWLEDGE_BASE as T;
                }

                const activeWorkspace = self.workspace as WorkspaceModelType | null;
                if (activeWorkspace && kbPages.includes(pageName as any)) {
                    const commonParams = pathParams as CommonParams | undefined;
                    if (commonParams?.libraryId) {
                        activeWorkspace.setSelectedLibraryId(commonParams.libraryId);
                    } else if (pageName === PAGES.DOC_EDIT || pageName === PAGES.DOC_HISTORY) {
                        const editDocParams = pathParams as EditDocParams | undefined;
                        const libraryId = editDocParams?.id
                            ? activeWorkspace.findLibraryIdForItem(editDocParams.id)
                            : null;

                        if (libraryId) {
                            activeWorkspace.setSelectedLibraryId(libraryId);
                        } else if (!activeWorkspace.selectedLibraryId && activeWorkspace.selectedLibrary) {
                            activeWorkspace.setSelectedLibraryId(activeWorkspace.selectedLibrary.id);
                        }
                    } else if (!activeWorkspace.selectedLibraryId && activeWorkspace.selectedLibrary) {
                        activeWorkspace.setSelectedLibraryId(activeWorkspace.selectedLibrary.id);
                    }
                }

                const redirected = self.redirectIfNeeded(pageName);
                if (redirected) {
                    return;
                }

                switch (pageName) {
                    case PAGES.KNOWLEDGE_BASE: {
                        setModal(null);
                        self.setPage(PAGES.KNOWLEDGE_BASE);
                        activeWorkspace?.setSelectedId(null);
                        toggleIsTreeCollapsed(false)
                        setRightMenu('');
                        break;
                    }
                    case PAGES.DOC_HISTORY:
                    case PAGES.DOC_EDIT: {
                        setModal(null);
                        const editDocParams = pathParams as EditDocParams
                        if (!activeWorkspace || editDocParams?.id === undefined) {
                            self.setPage(PAGES.KNOWLEDGE_BASE);
                            activeWorkspace?.setSelectedId(null);
                            setRightMenu('');
                        } else {
                            const wasOnTheSamePage = editDocParams.id === activeWorkspace.selectedId;
                            activeWorkspace?.setSelectedId(editDocParams.id);
                            self.setPage(pageName);
                            if (pageName === PAGES.DOC_HISTORY) {
                                if (activeWorkspace?.selectedId) {
                                    setRightMenu('history');
                                } else {
                                    setRightMenu('');
                                }
                            } else if (!wasOnTheSamePage) {
                                setRightMenu('');
                            }
                        }
                        if (isMobile) {
                            toggleIsTreeCollapsed(true);
                        }
                        break;
                    }
                    case PAGES.SETTINGS: {
                        setModal(null);
                        self.setPage(PAGES.SETTINGS_GENERAL);
                        break;
                    }
                    case PAGES.SETTINGS_MEMBERS: {
                        setModal(null);
                        self.setPage(PAGES.SETTINGS_MEMBERS);
                        break;
                    }
                    case PAGES.SETTINGS_GENERAL: {
                        setModal(null);
                        self.setPage(PAGES.SETTINGS_GENERAL);
                        break;
                    }
                    case PAGES.SETTINGS_BILLING: {
                        setModal(null);
                        self.setPage(PAGES.SETTINGS_BILLING);
                        break;
                    }
                    case PAGES.WORKSPACE_SETTINGS_GENERAL: {
                        setModal(null)
                        self.setPage(PAGES.WORKSPACE_SETTINGS_GENERAL)
                        break;
                    }
                    case PAGES.WORKSPACE_MEMBERS: {
                        setModal(null)
                        self.setPage(PAGES.WORKSPACE_MEMBERS)
                        break;
                    }
                    case PAGES.QUIZZES: {
                        setModal(null);
                        const userState = getEnv<RootStoreEnv>(self).getUserState();
                        registerQuizVisit(userState!.wsServerConnection);
                        self.setPage(PAGES.QUIZZES);
                        break;
                    }
                    case PAGES.QUIZ_VIEW:
                    case PAGES.QUIZ_TAKE:
                    case PAGES.QUIZ_EDIT: {
                        setModal(null);
                        const editDocParams = pathParams as EditTestParams
                        if (editDocParams.id === undefined) {
                            self.setPage(PAGES.QUIZZES);
                        } else {
                            if (pageName === PAGES.QUIZ_EDIT && userState?.isCurrentUserReadOnly) {
                                self.setPage(PAGES.QUIZ_VIEW);
                            } else {
                                self.setPage(pageName);
                            }
                        }
                        break;
                    }
                    case PAGES.ACKNOWLEDGMENTS_OVERVIEW: {
                        setModal(null);
                        self.setPage(PAGES.ACKNOWLEDGMENTS_OVERVIEW);
                        break;
                    }
                    case PAGES.LISTS: {
                        setModal(null);
                        self.setPage(PAGES.LISTS);
                        break;
                    }
                    case PAGES.CHARTFLOW: {
                        setModal(null);
                        self.setPage(PAGES.CHARTFLOW);
                        break;
                    }
                    case PAGES.SIGN_IN:
                        setModal({name: MODALS.LOGIN, params: undefined});
                        self.setPage(PAGES.SIGN_IN);
                        break;
                    case PAGES.SIGN_UP:
                        setModal({name: MODALS.SIGN_UP, params: {}});
                        self.setPage(PAGES.SIGN_UP);
                        break;
                    case PAGES.RESET_PASSWORD:
                        setModal({name: MODALS.FORGOT_PASSWORD, params: undefined});
                        self.setPage(PAGES.RESET_PASSWORD);
                        break;
                    default:
                        setModal(null);
                        self.setPage(pageName);
                        break;
                }
            },
        })
    );

export const isItemRequired = (pageName: string) => {
    return [PAGES.DOC_EDIT, PAGES.DOC_HISTORY].includes(pageName as any);
}

export type ViewStoreModel = Instance<typeof ViewStore>;
